import useTranslation from "next-translate/useTranslation"
import Link from "next/link"
import { Card, CardBody, CardHeader } from "reactstrap"

import { ChallengeType, ICategory, IProcess, UserRole } from "@api/schema"
import ActionIcon from "@components/common/ActionIcon"
import AuthElement from "@components/common/AuthElement"
import HtmlContent from "@components/common/HtmlContent"
import { Routes, routeWithParams } from "@services/routes"
import { prefixApiUrl } from "@services/util"
import { MODULE_CHALLENGE_AVAILABLE, MODULE_FUNDS_AVAILABLE, MODULE_EXTENDED_CATEGORIES_AVAILABLE } from "config"

import ProcessChallengeView from "./ProcessChallengeView"

interface IProps {
  categories: ICategory[]
  currentProcess: IProcess
  roles: UserRole[]
}

/**
 * this component shows the information about a participation process to the public
 * to be used in page/process/index
 */
const ProcessView: React.FC<IProps> = (props: IProps) => {
  const { categories, currentProcess, roles } = props
  const { t } = useTranslation("common")
  return <>
    <Card>
      <CardHeader>
        <h3>{currentProcess.name}</h3>
        <AuthElement requiredRole={UserRole.ProcessManager} roles={roles}>
          <div role="actions" className="icon-navigation">
            <ActionIcon
              aria-label={t("goto.management.editProcess")}
              href={Routes.ProcessEdit} /* as={routeWithParams(Routes.PROCESS_EDIT, {id: currentProcess.id})}*/
              title={t("goto.management.editProcess")}
              icon={"pencil"}
            />
          </div>
        </AuthElement>
      </CardHeader>
      <CardBody>
        <div className="rte-content">
          <div className="process-logo">
            <img
              src={currentProcess.logo
                ? prefixApiUrl(currentProcess.logo.contentUrl)
                : "/assets/img/placeholder/process-logo-placeholder.png"}
              alt={currentProcess.logo
                ? currentProcess.name
                : t("process.logo.placeholder")}
            />
            {/*
              *
              * <Image
              src={currentProcess.logo
                ? prefixApiUrl(currentProcess.logo.contentUrl)
                : "/assets/img/placeholder/process-logo-placeholder.png"}
              alt={currentProcess.logo
                ? currentProcess.logo.originalName
                : t("process.logo.placeholder")}
              layout="fill"
              objectFit="scale-down"
              />*/}
          </div>
          <HtmlContent content={currentProcess.description} />
        </div>
        <h5>{t("process.region")}</h5>
        {currentProcess.region}

        <h5>{t("process.goals")}</h5>
        {currentProcess.goals && currentProcess.goals.length > 0
          ? <ul>
            {currentProcess.goals.map((goal, index) => <li key={index}>{goal}</li>)}
          </ul>
          : t("default.empty")
        }

        {currentProcess.criteria && currentProcess.criteria.length > 0 && <>
          <h5>{t("process.criteria")}</h5>
          <ul>
            {currentProcess.criteria.map((criteria, index) => <li key={index}>{criteria}</li>)}
          </ul>
        </>}

        {categories?.length > 0 && <>
          <h5>{t("page.process.categories.title")}</h5>
          <AuthElement requiredRole={UserRole.ProcessManager} roles={roles}>
            <div role="actions" className="icon-navigation">
              <ActionIcon
                aria-label={t("management:goto.editCategories")}
                className="actionicon"
                href={routeWithParams(Routes.ProcessCategories, { id: currentProcess.id })}
                title={t("management:goto.editCategories")}
                icon={"pencil"}
                size={25}
              />
            </div>
          </AuthElement>
          <div>
            {categories.map((category) =>
              <span className="py-1" key={`category-${category.id}`}>
                <Link href={`${Routes.Marketplace}?category=${category.id}`}>
                  <a className="btn text-filteritem text-filteritem--small">
                    {category.title}
                  </a>
                </Link>
              </span>
            )}
          </div>

          {MODULE_EXTENDED_CATEGORIES_AVAILABLE && <div><Link href={Routes.Categories}>
            <a aria-label={t("goto.extendedCategories")} title={t("goto.extendedCategories")}>
              {t("goto.extendedCategories")}
            </a>
          </Link></div>}
        </>}
      </CardBody>
    </Card>
    <Card>
      <CardHeader>
        <h3>{t("process.imprint")}</h3>
      </CardHeader>
      <CardBody>
        <div className="rte-content">
          <HtmlContent content={currentProcess.imprint} />
        </div>
      </CardBody>
    </Card>


    {MODULE_CHALLENGE_AVAILABLE
      && <ProcessChallengeView
        challengeType={ChallengeType.BasicChallenge}
        selectedProcess={currentProcess}
        roles={roles}
      />}
    {MODULE_FUNDS_AVAILABLE
      && <ProcessChallengeView
        challengeType={ChallengeType.Fund}
        selectedProcess={currentProcess}
        roles={roles}
      />}
  </>
}

export default ProcessView
